import { useCallback, useState } from 'react';
import { Button, Form } from 'reactstrap';
import { AlertCircle, ChevronDown, ChevronUp, RotateCcw, Search } from 'react-feather';
import { NOTAS_D2L_HEADQUARTER, useNotasD2l } from '../../features/notasD2l';
import FiltrosNotasD2l from '../../components/notasAntiguoCampus/FiltrosNotasD2l';
import TablaNotasD2l from '../../components/notasAntiguoCampus/TablaNotasD2l';
import ModalComentarioNotaD2l from '../../components/notasAntiguoCampus/ModalComentarioNotaD2l';
import Loader from '../../layouts/loader/Loader';
import colors from '../../theme/colors';

const FILTROS_VACIOS = { nombre: '', apellido: '', oferta: '' };
const PANEL_FILTROS_ID = 'notas-d2l-filtros';

const normalizarFiltros = (valores) => ({
  nombre: valores.nombre.trim(),
  apellido: valores.apellido.trim(),
  oferta: valores.oferta.trim(),
});

const mismosFiltros = (a, b) =>
  Boolean(a && b) &&
  a.nombre === b.nombre &&
  a.apellido === b.apellido &&
  a.oferta === b.oferta;

const botonToolbarStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: '0.75rem',
  fontWeight: 600,
  borderRadius: '6px',
  transition: 'all 0.15s',
};

const botonLimpiarStyle = {
  ...botonToolbarStyle,
  padding: '6px 12px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#fff',
  color: '#6b7280',
  cursor: 'pointer',
};

const botonBuscarStyle = (deshabilitado) => ({
  ...botonToolbarStyle,
  padding: '6px 14px',
  border: 'none',
  backgroundColor: colors.primary,
  color: '#fff',
  cursor: deshabilitado ? 'not-allowed' : 'pointer',
  opacity: deshabilitado ? 0.5 : 1,
});

const avisoStyle = {
  marginTop: '16px',
  padding: '32px',
  textAlign: 'center',
  border: `1px solid ${colors.border}`,
  borderRadius: '8px',
  background: colors.bgCard,
  fontSize: '0.875rem',
};

const NotasAntiguoCampus = () => {
  const [showFilters, setShowFilters] = useState(true);
  const [borrador, setBorrador] = useState(FILTROS_VACIOS);
  const [busqueda, setBusqueda] = useState(null);
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);

  const {
    rows,
    hasMore,
    loadMore,
    isLoading,
    isFetching: buscando,
    isFetchingNextPage,
    isError,
    error,
    refetch,
  } = useNotasD2l({ headquarter: NOTAS_D2L_HEADQUARTER, filtros: busqueda });

  const cambiarFiltro = (campo, valor) => {
    setBorrador((prev) => ({ ...prev, [campo]: valor }));
  };

  const limpiarFiltros = () => {
    setBorrador(FILTROS_VACIOS);
  };

  const buscar = (e) => {
    e.preventDefault();
    if (buscando) return;
    const siguiente = normalizarFiltros(borrador);
    // Misma queryKey: sin refetch explícito, Buscar no relanzaría la consulta.
    if (mismosFiltros(siguiente, busqueda)) {
      refetch();
      return;
    }
    setBusqueda(siguiente);
  };

  const verComentario = useCallback((fila) => {
    setFilaSeleccionada(fila);
    setModalAbierto(true);
  }, []);

  const renderError = () => (
    <div role="alert" style={{ ...avisoStyle, color: colors.danger }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}
      >
        <AlertCircle size={16} aria-hidden="true" />
        <span>
          {error?.response?.data?.error || 'No se pudieron cargar las notas del campus anterior.'}
        </span>
      </div>
      <div>
        <Button color="light" size="sm" onClick={() => refetch()} disabled={buscando}>
          <RotateCcw size={14} aria-hidden="true" /> Reintentar
        </Button>
      </div>
    </div>
  );

  const renderResultados = () => {
    if (busqueda === null) return null;
    if (isLoading) {
      return (
        <div style={{ marginTop: '16px' }}>
          <Loader />
        </div>
      );
    }
    if (rows.length === 0) {
      if (isError) return renderError();
      return (
        <div style={{ ...avisoStyle, color: colors.textSecondary }}>
          No hay notas que coincidan con los filtros.
        </div>
      );
    }
    return (
      <>
        {isError && renderError()}
        <TablaNotasD2l
          rows={rows}
          onVerComentario={verComentario}
          hasMore={hasMore}
          onLoadMore={loadMore}
          isLoadingMore={isFetchingNextPage}
        />
      </>
    );
  };

  return (
    // .contentArea es un hijo flex sin min-width: 0, así que la tabla ancha lo
    // estiraría más allá de la pantalla. La rejilla minmax(0, 1fr) fija el ancho
    // al disponible y deja que ui/Table haga su scroll horizontal interno.
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <div style={{ marginBottom: '16px' }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text, margin: 0 }}>
          Notas antiguo campus
        </h1>
        <p
          style={{
            color: colors.textSecondary,
            marginTop: '2px',
            marginBottom: 0,
            fontSize: '0.8rem',
          }}
        >
          Consulta de calificaciones del campus anterior (D2L). Solo lectura.
        </p>
      </div>

      <Form onSubmit={buscar} role="search">
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
            overflow: 'visible',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '8px',
              padding: '12px 16px',
              borderBottom: showFilters ? '1px solid #f3f4f6' : 'none',
            }}
          >
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
              aria-controls={PANEL_FILTROS_ID}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#374151',
                padding: '4px 8px',
                borderRadius: '6px',
              }}
            >
              <Search size={15} style={{ color: '#9ca3af' }} aria-hidden="true" />
              Filtros
              {showFilters ? (
                <ChevronUp size={14} style={{ color: '#9ca3af' }} aria-hidden="true" />
              ) : (
                <ChevronDown size={14} style={{ color: '#9ca3af' }} aria-hidden="true" />
              )}
            </button>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={limpiarFiltros}
                disabled={buscando}
                style={botonLimpiarStyle}
              >
                <RotateCcw size={13} aria-hidden="true" />
                Limpiar
              </button>

              <button type="submit" disabled={buscando} style={botonBuscarStyle(buscando)}>
                <Search size={13} aria-hidden="true" />
                {buscando ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              id={PANEL_FILTROS_ID}
              style={{
                padding: '16px',
                opacity: buscando ? 0.5 : 1,
                pointerEvents: buscando ? 'none' : 'auto',
                transition: 'opacity 0.2s',
              }}
            >
              <FiltrosNotasD2l valores={borrador} onCambiar={cambiarFiltro} disabled={buscando} />
            </div>
          )}
        </div>
      </Form>

      {renderResultados()}

      <ModalComentarioNotaD2l
        isOpen={modalAbierto}
        fila={filaSeleccionada}
        onCerrar={() => setModalAbierto(false)}
        onClosed={() => setFilaSeleccionada(null)}
      />
    </div>
  );
};

export default NotasAntiguoCampus;
